import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { MdAutoAwesome, MdStar, MdTrendingUp, MdImage, MdTitle, MdDescription, MdTag } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getVideos, getChannels } from '../utils/api';

const platformIcons = {
  youtube: <FaYoutube style={{ color: '#ff0000', fontSize: '18px' }} />,
  facebook: <FaFacebook style={{ color: '#1877f2', fontSize: '18px' }} />,
  tiktok: <FaTiktok style={{ color: '#e0e0e0', fontSize: '18px' }} />,
  instagram: <FaInstagram style={{ color: '#e1306c', fontSize: '18px' }} />
};

const ScoreBar = ({ score, label, icon, color }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: 'var(--primary)', fontSize: '18px' }}>{icon}</span>
        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>{label}</span>
      </div>
      <span style={{ fontSize: '16px', fontWeight: '700', color: color || 'var(--primary)' }}>
        {score}/100
      </span>
    </div>
    <div style={{
      height: '8px', background: 'var(--bg-dark)',
      borderRadius: '4px', overflow: 'hidden',
      border: '1px solid var(--border)'
    }}>
      <div style={{
        height: '100%',
        width: `${score}%`,
        background: score >= 80
          ? 'linear-gradient(90deg, #00c853, #1de9b6)'
          : score >= 60
            ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
            : 'linear-gradient(90deg, #ef4444, #f97316)',
        borderRadius: '4px',
        transition: 'width 1s ease',
        boxShadow: `0 0 8px ${score >= 80 ? '#00c853' : score >= 60 ? '#f59e0b' : '#ef4444'}40`
      }} />
    </div>
  </div>
);

const VideoScorer = () => {
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const loadData = async () => {
    if (dataLoaded) return;
    setLoading(true);
    try {
      const [videosRes, channelsRes] = await Promise.all([getVideos(), getChannels()]);
      setVideos(videosRes.data.data || []);
      setChannels(channelsRes.data.data || []);
      setDataLoaded(true);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getChannelInfo = (channelId) => {
    return channels.find(c => c.id === channelId);
  };

  const analyseVideo = async () => {
        if (!selectedVideo) {
      toast.error('Please select a video first');
      return;
    }

    setScoring(true);
    setScoreData(null);

    const channel = getChannelInfo(selectedVideo.channel_id);

    const prompt = `You are an expert social media content analyst. Analyse this video and provide detailed scores and recommendations.

VIDEO DETAILS:
- Title: ${selectedVideo.youtube_title || selectedVideo.title || 'Not provided'}
- YouTube Description: ${selectedVideo.youtube_description || 'Not provided'}
- Facebook Title: ${selectedVideo.facebook_title || 'Not provided'}
- TikTok Caption: ${selectedVideo.tiktok_title || 'Not provided'}
- Instagram Caption: ${selectedVideo.instagram_title || 'Not provided'}
- Tags: ${selectedVideo.tags || 'Not provided'}
- Category: ${selectedVideo.category || 'Not provided'}
- Platform: ${channel?.platform || 'Unknown'}
- Channel: ${channel?.name || 'Unknown'}
- Thumbnail Available: ${selectedVideo.thumbnail_path ? 'Yes' : 'No'}

Respond ONLY in valid JSON with no other text:
{
  "overall_score": 75,
  "title_score": 80,
  "description_score": 70,
  "thumbnail_score": 60,
  "keywords_score": 75,
  "engagement_score": 65,
  "title_feedback": "Specific feedback about the title strength and improvements",
  "description_feedback": "Specific feedback about description quality",
  "thumbnail_feedback": "Specific feedback about thumbnail effectiveness",
  "keywords_feedback": "Specific feedback about keyword and tag usage",
  "engagement_feedback": "Specific feedback about engagement potential",
  "top_improvements": [
    "Most important improvement to make",
    "Second most important improvement",
    "Third most important improvement"
  ],
  "strengths": [
    "What is working well",
    "Another strength"
  ],
  "platform_specific": {
    "youtube": "YouTube specific advice",
    "facebook": "Facebook specific advice",
    "tiktok": "TikTok specific advice",
    "instagram": "Instagram specific advice"
  },
  "competitor_tips": "Tips on how to stand out from competitors in this niche",
  "viral_potential": "Assessment of viral potential and what would push it higher"
}`;

    try {
      const data = await callObsidianAI(
        [{ role: 'user', content: prompt }],
        '',
        2000
      );
      const cleanResponse = data.content[0].text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleanResponse);
      setScoreData(parsed);
      toast.success('Video analysis complete!');
      
    } catch (error) {
      toast.error('Failed to analyse video: ' + error.message);
    } finally {
      setScoring(false);
    }
  };

  const getOverallColor = (score) => {
    if (score >= 80) return '#00c853';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getOverallLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Needs Work';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><span className="gradient-text">ObsidianEye</span> 👁</h1>
        <p>Deep AI powered video analysis — scores, recommendations and insights for every platform</p>
      </div>

      <div className="grid-2">
        <div>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Select Video to Analyse</span>
            </div>

            {!dataLoaded ? (
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={loadData} disabled={loading}>
                {loading ? <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> Loading...</> : 'Load My Videos'}
              </button>
            ) : (
              <>
                <select
                  className="form-select"
                  style={{ marginBottom: '16px' }}
                  onChange={(e) => {
                    const video = videos.find(v => v.id === parseInt(e.target.value));
                    setSelectedVideo(video || null);
                    setScoreData(null);
                  }}
                >
                  <option value="">Select a video...</option>
                  {videos.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.youtube_title || v.title || `Video #${v.id}`}
                    </option>
                  ))}
                </select>

                {selectedVideo && (
                  <div style={{
                    background: 'var(--bg-dark)', borderRadius: '12px',
                    padding: '16px', marginBottom: '16px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                      {selectedVideo.youtube_title || selectedVideo.title || 'Untitled'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {selectedVideo.youtube_title && <span className="badge badge-youtube">YouTube Title</span>}
                      {selectedVideo.youtube_description && <span className="badge badge-youtube">Description</span>}
                      {selectedVideo.tags && <span className="badge badge-info">Tags</span>}
                      {selectedVideo.thumbnail_path && <span className="badge badge-success">Thumbnail</span>}
                      {!selectedVideo.thumbnail_path && <span className="badge badge-danger">No Thumbnail</span>}
                    </div>
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {platformIcons[getChannelInfo(selectedVideo.channel_id)?.platform]}
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {getChannelInfo(selectedVideo.channel_id)?.name}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
                  onClick={analyseVideo}
                  disabled={!selectedVideo || scoring}
                >
                  {scoring ? (
                    <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> Analysing...</>
                  ) : (
                    <><MdAutoAwesome /> Analyse Video</>
                  )}
                </button>
              </>
            )}
          </div>

          {scoreData && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Score Breakdown</span>
              </div>
              <ScoreBar score={scoreData.title_score} label="Title" icon={<MdTitle />} />
              <ScoreBar score={scoreData.description_score} label="Description" icon={<MdDescription />} />
              <ScoreBar score={scoreData.thumbnail_score} label="Thumbnail" icon={<MdImage />} />
              <ScoreBar score={scoreData.keywords_score} label="Keywords & Tags" icon={<MdTag />} />
              <ScoreBar score={scoreData.engagement_score} label="Engagement Potential" icon={<MdTrendingUp />} />
            </div>
          )}
        </div>

        <div>
          {scoreData ? (
            <>
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
                  <div style={{
                    width: '90px', height: '90px', borderRadius: '50%',
                    background: `conic-gradient(${getOverallColor(scoreData.overall_score)} ${scoreData.overall_score * 3.6}deg, var(--bg-dark) 0deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', flexShrink: 0
                  }}>
                    <div style={{
                      width: '70px', height: '70px', borderRadius: '50%',
                      background: 'var(--bg-card)',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '20px', fontWeight: '800', color: getOverallColor(scoreData.overall_score) }}>
                        {scoreData.overall_score}
                      </span>
                      <span style={{ fontSize: '10px', color: 'var(--text-light)' }}>/ 100</span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: getOverallColor(scoreData.overall_score) }}>
                      {getOverallLabel(scoreData.overall_score)}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Overall Video Score
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Top Improvements
                  </div>
                  {scoreData.top_improvements?.map((imp, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '10px', marginBottom: '8px',
                      padding: '10px 14px', background: 'rgba(239,68,68,0.08)',
                      borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)'
                    }}>
                      <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '14px' }}>{i + 1}</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{imp}</span>
                    </div>
                  ))}
                </div>

                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Strengths
                  </div>
                  {scoreData.strengths?.map((str, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '10px', marginBottom: '8px',
                      padding: '10px 14px', background: 'rgba(0,200,83,0.08)',
                      borderRadius: '8px', border: '1px solid rgba(0,200,83,0.2)'
                    }}>
                      <span style={{ color: '#00c853', fontWeight: '700' }}>✓</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{str}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="card-header">
                  <span className="card-title">Platform Specific Advice</span>
                </div>
                {scoreData.platform_specific && Object.entries(scoreData.platform_specific).map(([platform, advice]) => (
                  <div key={platform} style={{
                    padding: '14px', marginBottom: '10px',
                    background: 'var(--bg-dark)', borderRadius: '10px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      {platformIcons[platform]}
                      <span style={{ fontWeight: '600', textTransform: 'capitalize', fontSize: '14px' }}>
                        {platform}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {advice}
                    </p>
                  </div>
                ))}
              </div>

              <div className="card">
                <div className="card-header">
                  <span className="card-title">Viral Potential</span>
                  <MdStar style={{ color: 'var(--primary)', fontSize: '20px' }} />
                </div>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  {scoreData.viral_potential}
                </p>
                <div style={{
                  padding: '14px', background: 'rgba(0,200,83,0.08)',
                  borderRadius: '10px', border: '1px solid rgba(0,200,83,0.2)'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--primary)', marginBottom: '6px' }}>
                    COMPETITOR TIPS
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {scoreData.competitor_tips}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="card" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <MdAutoAwesome style={{ fontSize: '64px', color: 'var(--primary)', opacity: 0.3, marginBottom: '16px' }} />
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Ready to Score</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Select a video and click Analyse Video to get detailed scores and recommendations
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoScorer;